import { useState } from 'react';
import { Button, SegmentedControl, useToast } from '../ui';
import { slugify, standaloneDocument } from './articleHtml';
import type { ContentImage, ContentPiece } from '../../lib/contentPieces';

/**
 * PieceSource — read-only monospace view of the article, HTML or the writer's
 * markdown, with copy and "Download HTML" (a standalone document: doctype,
 * title, inline stylesheet, featured image as the first figure).
 */

export interface PieceSourceProps {
  piece: ContentPiece;
  /** Live html (may be newer than piece.html while an edit is saving). */
  html: string;
  featured: ContentImage | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function downloadStandalone(piece: ContentPiece, html: string, featured: ContentImage | null): string {
  const doc = standaloneDocument({
    title: piece.title,
    html,
    featured: featured?.selected_url ? { url: featured.selected_url, alt: featured.prompt || piece.title } : null,
  });
  const name = `${slugify(piece.title)}.html`;
  const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}

export default function PieceSource({ piece, html, featured }: PieceSourceProps) {
  const toast = useToast();
  const [view, setView] = useState<'html' | 'markdown'>('html');
  const text = view === 'html' ? html : piece.markdown;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(view === 'html' ? 'HTML copied' : 'Markdown copied');
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error("Couldn't copy", 'Select the text and copy it by hand.');
    }
  };

  const download = () => {
    const name = downloadStandalone(piece, html, featured);
    toast.success(`Downloaded ${name}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <SegmentedControl
          options={[
            { id: 'html', label: 'HTML' },
            { id: 'markdown', label: 'Markdown' },
          ]}
          value={view}
          onChange={(id) => setView(id as 'html' | 'markdown')}
        />
        <span style={{ flex: 1 }} />
        <Button size="sm" variant="ghost" icon="copy" onClick={copy}>
          Copy
        </Button>
        {/* icon prop, not a child svg: inside the label span the svg is display:block and
            stacked above the text */}
        <Button size="sm" variant="secondary" icon="download" onClick={download} title="Standalone .html file with the featured image and an inline stylesheet">
          Download HTML
        </Button>
      </div>
      {view === 'markdown' && !piece.markdown ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          No markdown source on this piece. The HTML is canonical.
        </p>
      ) : null}
      <pre className="ck-source" aria-label={view === 'html' ? 'Article HTML' : 'Article markdown'}>
        {text}
      </pre>
      <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
        HTML is the canonical body — it is what Preview renders, Edit writes back, and the download wraps. Markdown is
        the writer's original and does not change when you edit.
      </p>
    </div>
  );
}
